import { useCallback, useEffect, useRef, useState } from 'react'
import { Bot, FileText, Loader2, Paperclip, RotateCw, User, WifiOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { agentApi, type AgentMessage } from '@/lib/api/agent'
import { cn } from '@/lib/utils'
import { Composer } from './Composer'
import { ConfirmationCard } from './ConfirmationCard'
import { ArtifactPanel } from './ArtifactPanel'
import { ToolCallCard, type ToolCallView } from './ToolCallCard'
import type { MentionItem } from './MentionCombobox'
import { clockTime, dayLabel, startsNewDay } from './messageTime'
import { CONNECTION_LOST } from '@/src/lib/assistant/delivery'
import { proposalBatches, replyDraftRequest } from '@/src/lib/assistant/session'
import { ProposalCard } from './ProposalCard'
import { ReplyApprovalCard } from './ReplyApprovalCard'
import { useAssistant, type AssistantController } from './useAssistant'
import { VoicePanel } from './VoicePanel'

/** Voice needs a microphone and WebRTC, which browsers only give secure pages. */
export const voiceSupported =
  typeof window !== 'undefined' &&
  typeof RTCPeerConnection !== 'undefined' &&
  Boolean(navigator.mediaDevices?.getUserMedia)

export function DaySeparator({ iso }: { iso: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0 text-xs font-medium text-muted-foreground">{dayLabel(iso)}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

export function Bubble({ message }: { message: AgentMessage }) {
  const mine = message.role === 'USER'
  return (
    <div className={`flex gap-2 md:gap-3 ${mine ? 'justify-end' : ''}`}>
      {!mine && (
        <div className="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted">
          <Bot className="size-3.5 text-muted-foreground" />
        </div>
      )}
      <div
        className={`flex min-w-0 max-w-[88%] flex-col md:max-w-[80%] ${
          mine ? 'items-end' : 'items-start'
        }`}
      >
        <div
          className={`whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm ${
            mine ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}
        >
          {message.content}
        </div>
        <time
          dateTime={message.created_at}
          title={new Date(message.created_at).toLocaleString()}
          className="mt-1 px-1 text-[11px] tabular-nums text-muted-foreground"
        >
          {clockTime(message.created_at)}
        </time>
      </div>
      {mine && (
        <div className="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <User className="size-3.5 text-primary" />
        </div>
      )}
    </div>
  )
}

/**
 * One conversation, drawn from an assistant the caller holds. The widget and
 * the full page both draw the session's, so they show the same thing and
 * share one call.
 */
export function AssistantThreadView({
  assistant,
  mentionItems,
  layout = 'compact',
}: {
  assistant: AssistantController
  mentionItems: MentionItem[]
  /**
   * `compact` fills a small panel edge to edge (the widget). `page` is the full
   * page: the conversation in a readable centred column, the composer a card
   * at the bottom, nothing boxed in around them.
   */
  layout?: 'compact' | 'page'
}) {
  const {
    threadId,
    messages,
    confirmations,
    toolCalls,
    attachments,
    uploading,
    model,
    streamingText,
    streaming,
    error,
    fresh,
    voice,
  } = assistant
  const [showFiles, setShowFiles] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const conversationRef = useRef<HTMLDivElement>(null)
  /** Reply drafts waiting on the person, by job: their cards say when they are. */
  const [awaitingReplies, setAwaitingReplies] = useState<ReadonlySet<string>>(new Set())
  const replyAwaiting = useCallback((jobId: string, awaiting: boolean) => {
    setAwaitingReplies((current) => {
      if (current.has(jobId) === awaiting) return current
      const next = new Set(current)
      if (awaiting) next.add(jobId)
      else next.delete(jobId)
      return next
    })
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText, confirmations])

  const pending = confirmations.filter((item) => !item.confirmed_at && !item.rejected_at)
  const awaiting =
    assistant.proposals.filter((proposal) => proposal.status === 'PENDING').length +
    pending.length +
    awaitingReplies.size

  /** The first card waiting on the person, in view and focused, so Approve is a tap away. */
  const showAwaiting = useCallback((which: 'first' | 'last' = 'first') => {
    const cards = conversationRef.current?.querySelectorAll<HTMLElement>('[data-awaiting-approval]')
    const card = which === 'first' ? cards?.[0] : cards?.[cards.length - 1]
    if (!card) return
    card.scrollIntoView({ behavior: 'smooth', block: 'start' })
    card.focus({ preventScroll: true })
  }, [])

  // During a call, something new to approve comes into view by itself: the
  // person may be looking anywhere but the screen.
  const seenAwaiting = useRef(awaiting)
  useEffect(() => {
    const grew = awaiting > seenAwaiting.current
    seenAwaiting.current = awaiting
    if (!grew || !voice.active) return
    // After the card has rendered and the bottom scroll has run.
    const timer = setTimeout(() => showAwaiting('last'), 150)
    return () => clearTimeout(timer)
  }, [awaiting, voice.active, showAwaiting])
  const onConfirm = assistant.confirm
  const onReject = assistant.reject

  const callsByRun = new Map<string, ToolCallView[]>()
  for (const call of toolCalls) {
    if (!call.runId) continue
    const group = callsByRun.get(call.runId)
    if (group) group.push(call)
    else callsByRun.set(call.runId, [call])
  }

  const runOf = new Map(toolCalls.map((call) => [call.id, call.runId]))
  const placedRuns = new Set(
    messages.map((message) => message.run_id).filter((id): id is string => Boolean(id)),
  )
  // A run's cards go with its last message, once. A typed run has one reply; a
  // call's run has a line per thing said, and must not repeat them under each.
  const lastOfRun = new Map<string, string>()
  for (const message of messages) if (message.run_id) lastOfRun.set(message.run_id, message.id)
  const endsRun = (message: AgentMessage) =>
    Boolean(message.run_id) && lastOfRun.get(message.run_id ?? '') === message.id

  // One card per draft, however many times it was asked for or arrived.
  const shownDrafts = new Set<string>()
  const renderCall = (call: ToolCallView) => {
    // A proposal shows as its own card, not as the call that made it.
    if (call.toolName === 'propose_actions') return null
    const draft = replyDraftRequest(call)
    if (!draft) return <ToolCallCard key={call.id} call={call} />
    if (shownDrafts.has(draft.jobId)) return null
    shownDrafts.add(draft.jobId)
    return (
      <ReplyApprovalCard
        key={draft.jobId}
        jobId={draft.jobId}
        inquiryId={draft.inquiryId}
        onDecided={(summary, sent) => voice.reportDecision(summary, sent)}
        onAwaitingChange={(waiting) => replyAwaiting(draft.jobId, waiting)}
      />
    )
  }

  const batches = proposalBatches(assistant.proposals)
  const renderBatch = (batch: (typeof batches)[number]) => (
    <ProposalCard
      key={batch.batchId}
      proposals={batch.proposals}
      onApprove={assistant.approveProposals}
      onDismiss={assistant.dismissProposals}
    />
  )
  const batchesFor = (runId: string | null) =>
    runId ? batches.filter((batch) => batch.runId === runId) : []

  const confirmationsFor = (runId: string | null) =>
    runId ? pending.filter((item) => runOf.get(item.tool_call_id) === runId) : []

  // A run still streaming has no persisted reply yet, so its artifacts render
  // at the end until the message they belong to arrives.
  const unplacedCalls = toolCalls.filter((call) => !call.runId || !placedRuns.has(call.runId))
  const unplacedConfirmations = pending.filter((item) => {
    const runId = runOf.get(item.tool_call_id)
    return !runId || !placedRuns.has(runId)
  })

  const page = layout === 'page'

  const conversation = (
    <>
      {messages.length === 0 && !streaming && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <Bot className="mx-auto mb-3 size-6" />
          <p>Ask about an inquiry, or describe a new one.</p>
          <p className="mt-1 text-xs">Use @ to reference a consignment or inquiry.</p>
        </div>
      )}

      {messages.map((message, index) => (
        <div key={message.id} className="min-w-0 space-y-1.5">
          {/* A separator only where the day actually changes, so a long
              same-day conversation is not broken up for no reason. */}
          {startsNewDay(message.created_at, messages[index - 1]?.created_at) && (
            <DaySeparator iso={message.created_at} />
          )}
          {/* A run's tool calls belong above the reply they produced, not in a
              pile at the end of the thread. */}
          {endsRun(message) && callsByRun.get(message.run_id ?? '')?.map(renderCall)}
          <Bubble message={message} />
          {endsRun(message) &&
            confirmationsFor(message.run_id).map((confirmation) => (
              <ConfirmationCard
                key={confirmation.id}
                confirmation={confirmation}
                onConfirm={onConfirm}
                onReject={onReject}
              />
            ))}
          {endsRun(message) && batchesFor(message.run_id).map(renderBatch)}
        </div>
      ))}

      {unplacedCalls.length > 0 && (
        <div className="min-w-0 space-y-1.5">{unplacedCalls.map(renderCall)}</div>
      )}

      {streamingText && (
        <Bubble
          message={{
            id: 'streaming',
            thread_id: threadId ?? '',
            role: 'ASSISTANT',
            content: streamingText,
            references: [],
            run_id: null,
            created_at: new Date().toISOString(),
          }}
        />
      )}

      {/* Sent, and Beacon has not started answering yet. */}
      {streaming && !streamingText && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 aria-hidden className="size-3.5 animate-spin" />
          {assistant.reconnecting ? 'Reconnecting to Beacon…' : 'Beacon is working on it…'}
        </p>
      )}

      {assistant.lost && !streaming && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-md border border-waiting/50 bg-waiting/5 px-3 py-2 text-sm"
        >
          <WifiOff aria-hidden className="size-4 shrink-0 text-waiting" />
          <span className="min-w-0 flex-1">{CONNECTION_LOST}</span>
          <Button size="sm" variant="outline" onClick={() => void assistant.retry()}>
            <RotateCw />
            Retry
          </Button>
        </div>
      )}

      {unplacedConfirmations.map((confirmation) => (
        <ConfirmationCard
          key={confirmation.id}
          confirmation={confirmation}
          onConfirm={onConfirm}
          onReject={onReject}
        />
      ))}

      {/* Still streaming, or proposed during a call: shown at the end until placed. */}
      {batches.filter((batch) => !batch.runId || !placedRuns.has(batch.runId)).map(renderBatch)}

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div ref={bottomRef} />
    </>
  )

  const filesToggle = (
    <button
      type="button"
      onClick={() => setShowFiles((open) => !open)}
      aria-expanded={showFiles}
      className={cn(
        'flex items-center gap-2 text-left text-xs text-muted-foreground',
        page
          ? 'rounded-full px-2 py-1 hover:text-foreground'
          : 'border-t border-border px-4 py-2 hover:bg-muted/50',
      )}
    >
      <Paperclip className="size-3.5" />
      {attachments.length === 0
        ? 'No files'
        : `${attachments.length} file${attachments.length === 1 ? '' : 's'}`}
      {/* Read from the run record, not from asking the model - it does not
          reliably know its own name. */}
      {model && <span className="ml-auto tabular-nums">{model}</span>}
    </button>
  )

  const voicePanel = voice.status !== 'idle' && (
    <VoicePanel
      status={voice.status}
      lines={voice.lines}
      muted={voice.muted}
      error={voice.error}
      onToggleMute={voice.toggleMute}
      onEnd={voice.active ? voice.stop : voice.dismiss}
      awaiting={awaiting}
      onShowAwaiting={() => showAwaiting('first')}
      className={page ? 'rounded-xl border' : undefined}
    />
  )

  const composer = (
    <Composer
      onSend={(content) => void assistant.send(content)}
      onStop={assistant.stop}
      streaming={streaming}
      mentionItems={mentionItems}
      uploading={uploading}
      onUpload={assistant.uploadFiles}
      attachedCount={fresh.length}
      onVoice={voiceSupported ? assistant.startVoice : undefined}
      voiceActive={voice.active}
      variant={page ? 'card' : 'bar'}
      // A call needs the room for the conversation more than for typing.
      rows={page || voice.active ? 1 : 2}
      above={page ? <FreshAttachments assistant={assistant} className="px-3 pt-3" /> : undefined}
    />
  )

  if (page) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div ref={conversationRef} className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl min-w-0 space-y-4 px-1 py-4 md:px-4 md:py-6">
            {conversation}
          </div>
        </div>
        <div className="mx-auto w-full max-w-3xl space-y-2 px-1 pt-2 md:px-4">
          {showFiles && (
            <div className="max-h-64 overflow-y-auto rounded-xl border border-border bg-card">
              <ArtifactPanel attachments={attachments} />
            </div>
          )}
          {voicePanel}
          {composer}
          <div className="flex">{filesToggle}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={conversationRef}
        className="min-h-0 min-w-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto p-3 md:p-4"
      >
        {conversation}
      </div>

      {showFiles && (
        <div className="max-h-64 overflow-y-auto border-t border-border">
          <ArtifactPanel attachments={attachments} />
        </div>
      )}

      {filesToggle}

      {voicePanel}

      <FreshAttachments assistant={assistant} className="border-t border-border px-3 pt-3" />

      {composer}
    </div>
  )
}

/** The files going with the next message, as thumbnails. Nothing when there are none. */
export function FreshAttachments({
  assistant,
  className,
}: {
  assistant: Pick<AssistantController, 'attachments' | 'fresh'>
  className?: string
}) {
  const { attachments, fresh } = assistant
  if (fresh.length === 0) return null
  return (
    <div
      aria-label="Attached to your next message"
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {attachments
        .filter((attachment) => fresh.includes(attachment.id))
        .map((attachment) => (
          <figure
            key={attachment.id}
            className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-muted/40 p-1.5 pr-2 text-xs"
          >
            {attachment.content_type.startsWith('image/') ? (
              <img
                src={agentApi.attachmentUrl(attachment.id)}
                alt=""
                className="size-10 rounded object-cover"
              />
            ) : (
              <FileText className="size-4 text-muted-foreground" />
            )}
            <figcaption className="max-w-40 truncate">{attachment.file_name}</figcaption>
          </figure>
        ))}
      <p className="w-full text-xs text-muted-foreground">
        Beacon reads {fresh.length === 1 ? 'this' : 'these'} with your next message.
      </p>
    </div>
  )
}

/**
 * One conversation on its own, for a screen that keeps no shared session: the
 * conversation lives and ends with this component.
 */
export function AssistantThread({
  mentionItems,
  ...options
}: {
  threadId: string | null
  onThreadCreated: (threadId: string) => void
  /** Beacon gave the conversation a title; the list can show it at once. */
  onThreadTitled?: (threadId: string, title: string) => void
  mentionItems: MentionItem[]
  contextType?: string
  contextId?: string
}) {
  const assistant = useAssistant(options)
  return <AssistantThreadView assistant={assistant} mentionItems={mentionItems} />
}
